import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserAvatar } from '@shared/components/ui/UserAvatar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage } from '@fortawesome/pro-thin-svg-icons';
import { useProfile } from '../../hooks/useProfile';
import { useUpdateProfile } from '../../hooks/useUpdateProfile';
import { useToast } from '@shared/hooks/use-toast';

export const Avatar = () => {
  const { t } = useTranslation('profileModule');
  const { data: profile } = useProfile();
  const { updateUser, isLoading } = useUpdateProfile();
  const { toast } = useToast();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);

  if (!profile) return null;

  const { name, profile_photo_path } = profile.user;

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    try {
      await updateUser({
        profile_photo_file: file,
      });

      toast({
        title: t('avatar.upload_success'),
        description: t('avatar.uploaded'),
      });
    } catch (error: any) {
      // �� Log para debugging - información estructurada para identificar problemas
      console.log('🔍 Avatar - Error:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
      });

      // 1. Mensaje por defecto (genérico) - fallback de seguridad
      let errorMessage = t('avatar.upload_failed');

      // 2. Si hay mensaje específico del servidor, analizarlo
      if (error?.response?.data?.message) {
        const serverMessage = error.response.data.message;

        // 3. Determinar tipo específico de error según el contenido del mensaje
        if (serverMessage.includes('profile_photo_path') || serverMessage.includes('too long')) {
          // Error de URL corrupta o datos muy largos - problema del servidor
          errorMessage = 'Error en el servidor: Problema con la imagen de perfil. Contacta al administrador.';
        } else if (serverMessage.includes('file') || serverMessage.includes('image')) {
          // Error relacionado con el archivo de imagen (formato, tamaño, etc.)
          errorMessage = 'Error al subir la imagen. Verifica el formato y tamaño del archivo.';
        } else {
          // 4. Si no coincide con ningún patrón específico, truncar mensajes largos
          // para evitar mostrar URLs corruptas o errores técnicos muy extensos
          errorMessage = serverMessage.length > 200 ? serverMessage.substring(0, 200) + '...' : serverMessage;
        }
      } else if (error?.message) {
        // 5. Si no hay mensaje del servidor pero hay error.message (errores de red, etc.)
        errorMessage = error.message;
      }
      // 6. Si no hay nada, se mantiene el mensaje genérico inicial

      toast({
        title: t('avatar.upload_error'),
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  return (
    <>
      <div className={`relative transition-opacity duration-300 ${isLoading ? 'opacity-70' : 'opacity-100'}`}>
        <UserAvatar name={name} image={preview || profile_photo_path || ''} size="lg" />

        <button
          type="button"
          onClick={handleImageClick}
          className="absolute bottom-0 right-0 flex h-8 w-8 translate-x-1/4 translate-y-1/4 items-center justify-center rounded-full border border-border bg-background shadow-sm transition-colors hover:bg-accent"
          title={t('avatar.edit_image')}
          disabled={isLoading}
        >
          <FontAwesomeIcon icon={faImage} className="h-3.5 w-3.5 text-muted-foreground" />
        </button>

        <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" accept="image/*" />
      </div>
    </>
  );
};
